import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Markdown } from '@/components/ui/markdown';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { AttachmentsPanel } from '@/features/documents';
import { formatMoney } from '@/lib/money';
import { OFFER_STATUS_LABEL, type OfferStatus } from '../constants';
import { useCandidate, useDeleteOffer, useOffer, useOpening } from '../queries';
import { candidateName } from '../types';

const OFFERS_PATH = '/hrm/recruitment/offers';

const STATUS_VARIANT: Record<OfferStatus, 'secondary' | 'destructive' | 'outline'> = {
  draft: 'outline',
  sent: 'secondary',
  accepted: 'secondary',
  declined: 'destructive',
};

export function OfferDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: offer, isPending, isError, error } = useOffer(id);
  const candidate = useCandidate(offer?.candidate ?? '');
  const opening = useOpening(offer?.job_opening ?? '');
  const remove = useDeleteOffer();

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(OFFERS_PATH)}>
          <ArrowLeft /> Offers
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const name = candidate.data ? candidateName(candidate.data) : `Candidate #${offer.candidate}`;
  const money = formatMoney(offer.amount, offer.currency, { narrowSymbol: true });

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(OFFERS_PATH)}>
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Offer — {name}</h1>
        <Badge variant={STATUS_VARIANT[offer.status]}>{OFFER_STATUS_LABEL[offer.status]}</Badge>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => navigate(`${OFFERS_PATH}/${offer.public_id}/edit`)}>
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete offer?</AlertDialogTitle>
                <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(offer.id, { onSuccess: () => navigate(OFFERS_PATH) })
                  }
                >
                  {remove.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Candidate">{name}</Field>
          <Field label="Applied for">
            {offer.job_opening ? (
              <Button
                variant="link"
                className="h-auto p-0"
                disabled={!opening.data}
                onClick={() =>
                  opening.data && navigate(`/hrm/recruitment/${opening.data.public_id}`)
                }
              >
                {opening.data?.title ?? `Opening #${offer.job_opening}`}
              </Button>
            ) : (
              '—'
            )}
          </Field>
          <Field label="Amount">{money ?? '—'}</Field>
          <Field label="Sent">
            {offer.sent_at ? new Date(offer.sent_at).toLocaleDateString() : '—'}
          </Field>
          <Field label="Responded">
            {offer.responded_at ? new Date(offer.responded_at).toLocaleDateString() : '—'}
          </Field>
          <Field label="Created">{new Date(offer.created).toLocaleDateString()}</Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Description</CardTitle>
        </CardHeader>
        <CardContent>
          {offer.description ? (
            <Markdown>{offer.description}</Markdown>
          ) : (
            <p className="text-muted-foreground text-sm">No description.</p>
          )}
        </CardContent>
      </Card>

      <AttachmentsPanel modelLabel="recruitment.offer" objectId={offer.id} />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
